import { useEffect, useReducer, useRef } from 'react';
const FETCH = 'FETCH';
const FETCH_SUCCESS = 'FETCH_SUCCESS';
const FETCH_FAIL = 'FETCH_FAIL';

export type Status = 'idle' | 'loading' | 'success' | 'error';
type Action<Response> =
  | { type: typeof FETCH }
  | { type: typeof FETCH_SUCCESS; json: Response }
  | { type: typeof FETCH_FAIL; error: string };

type State<Response> = {
  error: string;
  json: Response;
  status: Status;
};

const initialState = {
  error: '',
  json: {},
  status: 'idle' as Status,
};

const createReducer = <Response>() => (
  state: State<Response>,
  action: Action<Response>
) => {
  switch (action.type) {
    case FETCH:
      return { ...state, error: '', status: 'loading' as Status };
    case FETCH_SUCCESS:
      return {
        ...state,
        json: action.json,
        status: 'success' as Status,
      };
    case FETCH_FAIL:
      return {
        ...state,
        error: action.error,
        status: 'error' as Status,
      };
  }
};

type UseFetchDataParameters<Parameters, Response> = {
  fetchFunction: (parameters: Parameters) => Promise<Response>;
  parameters?: Parameters;
  dependencies?: any[];
  options?: { fetchOnFirstRender: boolean };
};

export function useFetchData<Parameters, Response>({
  fetchFunction,
  parameters = {} as Parameters,
  dependencies = [],
  options = {
    fetchOnFirstRender: true,
  },
}: UseFetchDataParameters<Parameters, Response>): {
  state: { error: string; status: Status; json: Response };
  fetchData: () => void;
} {
  const canFetch = useRef(options.fetchOnFirstRender);
  const reducer = createReducer<Response>();
  const [state, dispatch] = useReducer(reducer, initialState as State<
    Response
  >);
  async function fetchData() {
    dispatch({ type: FETCH });
    try {
      const json = await fetchFunction(parameters);
      dispatch({ type: FETCH_SUCCESS, json });
    } catch (err) {
      dispatch({ type: FETCH_FAIL, error: err.message });
    }
  }
  useEffect(() => {
    if (canFetch && canFetch.current) {
      fetchData();
    }
    canFetch.current = true;
  }, [...dependencies]);
  return { state, fetchData };
}
